import { Text } from '../Text';
import type { TextExtension } from '../types';

import type { PathExtension } from './path';
import { asPath } from './path';

/**
 * Extension interface providing URL parsing and manipulation methods.
 * Extends {@link PathExtension} with absolute-URL-specific properties.
 */
export interface URLExtension extends PathExtension {
  readonly parsed: Readonly<
    Pick<
      URL,
      | 'href'
      | 'protocol'
      | 'hostname'
      | 'pathname'
      | 'search'
      | 'hash'
      | 'origin'
    >
  >;

  /**
   * Creates a new URL with additional or modified query parameters.
   * Existing parameters are preserved unless overwritten.
   *
   * @param params - Key-value pairs to add or update in the query string.
   * @returns A new URL with the updated query parameters.
   *
   * @example
   * const t = $$$('https://example.com?a=1', asURL());
   * t.withParams({ b: '2' }); // URL for 'https://example.com?a=1&b=2'
   * t.withParams({ a: '99' }); // URL for 'https://example.com?a=99'
   */
  withParams(params: Record<string, string | number | undefined>): URLExtension;
}

/**
 * Creates an extension that parses and manipulates URLs.
 */
export function asURL(): TextExtension<URLExtension> {
  return (text) => {
    const parsed = new URL(text.$$);
    // eslint-disable-next-line @typescript-eslint/unbound-method -- asPath methods close over local state, not `this`
    const { getParams: pathGetParams, withParams: pathWithParams } =
      text.extend(asPath());

    return {
      get parsed() {
        return parsed;
      },

      withParams(
        params: Record<string, string | number | undefined>,
      ): URLExtension {
        return Text.of(pathWithParams(params).$$).extend(asURL());
      },

      getParams(): Record<string, string> {
        return pathGetParams();
      },
    };
  };
}
